/**
 * Client-generated globally unique IDs that match the backend's scheme in
 * backend/src/guid.ts: a UUID v1 (time-based, approximately lexicographically
 * sortable) encoded as base62 with the alphabet [0-9a-zA-Z].
 *
 * The atomic move endpoint POST /v3/grazingEvents/move treats the client guid
 * as the idempotency key, so the client must mint it in the same shape the rest
 * of the system uses rather than an RFC 4122 hyphenated UUID v4.
 */
import { v1 as uuidv1 } from "uuid";

const ALPHABET =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const BASE = ALPHABET.length;
const LEADER = ALPHABET.charAt(0);
const FACTOR = Math.log(256) / Math.log(BASE);

// Big-endian base conversion identical to the `base-x` package the backend
// uses, so a value encoded here matches one encoded by backend/src/guid.ts.
// base-x is not a frontend dependency, so the algorithm is inlined.
function base62Encode(source: Uint8Array): string {
  if (source.length === 0) return "";

  let zeroes = 0;
  let length = 0;
  let pbegin = 0;
  const pend = source.length;
  while (pbegin !== pend && source[pbegin] === 0) {
    pbegin++;
    zeroes++;
  }

  const size = ((pend - pbegin) * FACTOR + 1) >>> 0;
  const buffer = new Uint8Array(size);

  while (pbegin !== pend) {
    let carry = source[pbegin];
    let i = 0;
    for (
      let it = size - 1;
      (carry !== 0 || i < length) && it !== -1;
      it--, i++
    ) {
      carry += (256 * buffer[it]) >>> 0;
      buffer[it] = carry % BASE >>> 0;
      carry = (carry / BASE) >>> 0;
    }
    length = i;
    pbegin++;
  }

  let it = size - length;
  while (it !== size && buffer[it] === 0) it++;

  let str = LEADER.repeat(zeroes);
  for (; it < size; ++it) str += ALPHABET.charAt(buffer[it]);
  return str;
}

export function guid(): string {
  const buffer = new Uint8Array(16);
  uuidv1(undefined, buffer, 0);
  return base62Encode(buffer);
}
